<template>
  <div class="bg-surface-default-low flex flex-col gap-32 p-24">
    <div class="flex justify-between gap-32">
      <div class="flex flex-col gap-16">
        <div class="flex flex-col">
          <h2 class="heading-2">
            {{
              i18n(translations.numberOfOpportunities, {
                numberOfOpportunities,
              })
            }}
          </h2>
          <p class="body-2">{{ i18n(translations.description) }}</p>
        </div>
        <div class="flex gap-16">
          <RevButtonBase
            class="body-2-bold underline"
            @click="emit('see-more-margin')"
          >
            {{ i18n(translations.seeMoreMargin) }}
          </RevButtonBase>
          <RevButtonBase
            class="body-2-bold underline"
            @click="emit('see-more-average-selling-price')"
          >
            {{ i18n(translations.seeMoreAverageSellingPrice) }}
          </RevButtonBase>
        </div>
        <div v-if="readableLastUpdatedAt" class="flex gap-4">
          <IconInfo />
          <span class="body-2">{{
            i18n(translations.lastUpdatedAt, {
              lastUpdatedAt: readableLastUpdatedAt,
            })
          }}</span>
        </div>
      </div>
      <div>
        <RevInputSelect
          id="page-size"
          v-model="pageSizeInput"
          class="w-160"
          :label="i18n(translations.pageSize)"
          :options="pageSizeOptions"
          @update:model-value="handlePageSizeChange"
        />
      </div>
    </div>

    <div v-if="isLoading">
      <RevLoadingScreen class="h-640" :text="i18n(translations.loading)" />
    </div>
    <OpportunitiesDataTable
      v-else
      v-model:sort-by-input="sortByInput"
      v-model:sort-order-input="sortOrderInput"
      :opportunities
    />

    <Pagination
      :current-page="pageNumberInput"
      :page-count
      @click="handlePagination"
    />
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'

import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { useTracking } from '@backmarket/nuxt-module-tracking/useTracking'
import { RevButtonBase } from '@ds/components/ButtonBase'
import { RevInputSelect, type Value } from '@ds/components/InputSelect'
import { RevLoadingScreen } from '@ds/components/LoadingScreen'
import { IconInfo } from '@ds/icons/IconInfo'

import type { SORT_ORDER_ENUM, SourcingOpportunity } from '~/api/quotes/types'
import Pagination from '~/components/Pagination/Pagination.vue'

import { useOpportunitiesFilters } from '../composables/useOpportunitiesFilters'

import OpportunitiesDataTable from './OpportunitiesDataTable.vue'
import translations from './OpportunitiesTable.translations'

const i18n = useI18n()
const { trackClick } = useTracking()

const props = defineProps<{
  opportunities: SourcingOpportunity[]
  pageCount: number
  lastUpdatedAt: string | null
  numberOfOpportunities: number
  isLoading: boolean
}>()

const emit = defineEmits(['see-more-margin', 'see-more-average-selling-price'])

const sortOrderInput = defineModel<SORT_ORDER_ENUM | undefined>(
  'sortOrderInput',
  {
    required: true,
  },
)
const sortByInput = defineModel<string>('sortByInput', { required: true })
const pageNumberInput = defineModel<number>('pageNumberInput', {
  required: true,
})
const pageSizeInput = defineModel<string>('pageSizeInput', { required: true })

const { pageSizeOptions } = useOpportunitiesFilters()

const readableLastUpdatedAt = computed(() => {
  if (!props.lastUpdatedAt) return ''

  const date = new Date(props.lastUpdatedAt)

  return i18n.date(date, {
    year: 'numeric',
    month: 'numeric',
    day: 'numeric',
    hour: 'numeric',
    minute: 'numeric',
  })
})
function handlePagination({ page }: { page: number }) {
  trackClick({
    name: 'navigate_to_page',
    zone: 'opportunities_filters',
    value: page,
  })

  pageNumberInput.value = page
}

function handlePageSizeChange(value: Value) {
  if (typeof value === 'string') {
    trackClick({
      name: 'change_page_size',
      zone: 'opportunities_filters',
      value,
    })
  }
}
</script>
